/**
 * RepresentantesTab - Tab for representatives management
 */
import React, { useMemo } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef,
} from 'mantine-react-table';
import type { Representante } from '../../hooks/useHospitalData';
import { MRT_LOCALIZATION_PT_BR } from './tableLocalization';
import PermissionGate from '../PermissionGate';

interface RepresentantesTabProps {
  representantes: Representante[];
  onExportRepresentantes: () => void;
}

export const RepresentantesTab: React.FC<RepresentantesTabProps> = ({
  representantes,
  onExportRepresentantes,
}) => {
  // Columns definition
  const columns = useMemo<MRT_ColumnDef<Representante>[]>(
    () => [
      {
        accessorKey: 'cliente_razao_social',
        header: 'Cliente',
        accessorFn: (row: any) => row.cliente_nome_fantasia || row.cliente_razao_social || '-',
      },
      {
        accessorKey: 'nome',
        header: 'Representante',
      },
      {
        accessorKey: 'email',
        header: 'Email',
        accessorFn: (row) => row.email || '-',
      },
      {
        accessorKey: 'telefone',
        header: 'Telefone',
        accessorFn: (row: any) => row.telefone || row.celular || '-',
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: representantes,
    enableColumnFilters: true,
    enableSorting: true,
    enablePagination: true,
    enableRowActions: false,
    positionActionsColumn: 'last',
    enableDensityToggle: false,
    initialState: {
      showColumnFilters: true,
      density: 'xs',
    },
    mantineTableProps: {
      highlightOnHover: true,
      striped: true,
      withTableBorder: true,
      withColumnBorders: true,
      sx: {
        'th': {
          padding: '8px 12px !important',
        },
        'td': {
          padding: '4px 12px !important',
        }
      }
    },
    mantinePaperProps: {
      shadow: 'none',
      withBorder: true,
      radius: 'md',
    },
    localization: MRT_LOCALIZATION_PT_BR,
  });

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Representantes</h2>
        <div className="flex items-center gap-2">
          <PermissionGate tela="HOSPITAIS_LIST" acao="exportar">
            <button
              onClick={onExportRepresentantes}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-1.5"
            >
              <FileSpreadsheet className="w-4 h-4" /> Exportar
            </button>
          </PermissionGate>
        </div>
      </div>

      <div className="p-0">
        <MantineReactTable table={table} />
      </div>
    </div>
  );
};
